import React from "react";
import { Link } from "react-router-dom";
import { BlogPost } from "@/types/blog";
import { Calendar, Clock, Eye, Heart, MessageCircle, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";

interface BlogCardProps {
  post: BlogPost;
  variant?: "default" | "featured" | "compact";
}

const BlogCard: React.FC<BlogCardProps> = ({ post, variant = "default" }) => {
  const isFeatured = variant === "featured";
  const isCompact = variant === "compact";
  const fallbackImage = "/assets/graphics/hero1.png";
  const [imageError, setImageError] = React.useState(false);

  // Use image_url as primary source, fallback to legacy fields if needed
  const imageUrl = post.image_url || post.coverImage || post.thumbnail;

  return (
    <article className={cn(
      "group overflow-hidden rounded-lg border bg-card transition-all hover:shadow-lg",
      isFeatured ? "flex flex-col md:flex-row" : "flex flex-col",
      post.isPremium && "border-sauna-orange/50"
    )}>
      <Link to={`/blog/${post.slug}`} className={cn(
        "block overflow-hidden relative",
        isFeatured ? "md:w-1/2" : "w-full"
      )}>
        <div className={cn(
          "aspect-video w-full overflow-hidden bg-muted relative",
          isCompact ? "aspect-[3/2]" : ""
        )}>
          <img
            src={imageError || !imageUrl ? fallbackImage : imageUrl}
            alt={post.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            width={400}
            height={225}
            onError={() => {
              console.warn(`Failed to load image for blog post: ${post.title}`);
              setImageError(true);
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          {post.isPremium && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white">
                <Star className="h-3 w-3 mr-1" />
                Premium
              </Badge>
            </div>
          )}
          
          {post.featured && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-gradient-to-r from-sauna-blue to-sauna-blue/80 text-white">
                Featured
              </Badge>
            </div>
          )}
        </div>
      </Link>
      
      <div className={cn(
        "flex flex-col p-4",
        isFeatured ? "md:w-1/2 md:p-6" : "",
        isCompact ? "p-3" : ""
      )}>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge 
            variant="outline" 
            className="text-xs font-medium bg-gradient-to-r from-sauna-orange/10 to-sauna-blue/10 text-sauna-navy border-none"
          >
            {post.category?.name || 'Uncategorized'}
          </Badge>
          
          <div className="flex items-center text-xs text-muted-foreground">
            <Calendar className="mr-1 h-3 w-3" />
            <time dateTime={post.publishedAt}>{post.publishedAt ? formatDate(post.publishedAt) : 'N/A'}</time>
          </div>
          
          {post.lastUpdated && (
            <div className="text-xs text-muted-foreground">
              Updated {formatDate(post.lastUpdated)}
            </div>
          )}
        </div>
        
        <Link to={`/blog/${post.slug}`}>
          <h3 className={cn(
            "font-semibold text-sauna-navy hover:text-sauna-blue transition-colors",
            isFeatured ? "text-2xl" : isCompact ? "text-base line-clamp-2" : "text-xl"
          )}>
            {post.title}
          </h3>
        </Link>
        
        {!isCompact && (
          <p className={cn(
            "mt-2 line-clamp-3 text-gray-600",
            isFeatured ? "line-clamp-4" : ""
          )}>
            {post.excerpt}
          </p>
        )}
        
        {!isCompact && post.tags && post.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {post.tags.slice(0, 3).map((tag) => (
              <Badge 
                key={tag} 
                variant="secondary" 
                className="text-xs bg-sauna-beige/50 hover:bg-sauna-beige"
              >
                #{tag}
              </Badge>
            ))}
            {post.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs bg-sauna-beige/50">
                +{post.tags.length - 3} more
              </Badge>
            )}
          </div>
        )}
        
        {!isCompact && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {post.readTime && (
                <span className="flex items-center">
                  <Clock className="mr-1 h-3 w-3" />
                  {post.readTime} min read
                </span>
              )}
              {post.views !== undefined && (
                <span className="flex items-center">
                  <Eye className="mr-1 h-3 w-3" />
                  {post.views}
                </span>
              )}
              {post.likes !== undefined && (
                <span className="flex items-center">
                  <Heart className="mr-1 h-3 w-3" />
                  {post.likes}
                </span>
              )}
              {post.comments !== undefined && (
                <span className="flex items-center">
                  <MessageCircle className="mr-1 h-3 w-3" />
                  {post.comments}
                </span>
              )}
            </div>
            
            <Link 
              to={`/blog/${post.slug}`}
              className="text-sm font-medium text-sauna-blue hover:text-sauna-orange transition-colors group-hover:underline"
            >
              Read more
            </Link>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogCard;
